import { FC, PropsWithChildren, ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Combobox, ComboboxOption } from "@/components/_molecules/combobox/combobox";
import { useGetFocusAreaList } from "@/domain/focus-area/hooks/api/use-get-focus-area-list";
import { compareFractionalOrder } from "@/lib/compare-fractional-order";

import { FocusArea } from "../models/focus-area-model";

import { FocusAreaSelectDropdownItem } from "./focus-are-select-dropdown-item";

const INVALID_ID = "-1";

export interface FocusAreaSelectDropdownProps extends PropsWithChildren {
  workspaceId: string;
  asChild?: boolean;
  selected?: {
    focusAreaId: string;
  } | null;
  onSelect?: (focusArea: { id: string } | null) => void;
  side?: "top" | "right" | "bottom" | "left";
  isReadOnly?: boolean;
}

export const FocusAreaSelectDropdown: FC<FocusAreaSelectDropdownProps> = ({
  workspaceId,
  children,
  side,
  asChild,
  isReadOnly,
  onSelect,
  selected,
}) => {
  const { t } = useTranslation("focus-area");
  const [selectedFocusArea, setSelectedFocusArea] = useState(selected ? selected : null);
  const { focusAreaList } = useGetFocusAreaList({ workspaceId });

  const comboboxOptions = useMemo(() => {
    const focusAreaOptions: ComboboxOption<FocusArea>[] = focusAreaList
      .slice()
      .sort((a, b) => {
        return compareFractionalOrder(a.order, b.order);
      })
      .flatMap((u) => {
        if (!u) {
          return [];
        }
        return {
          id: u.id,
          value: u.name,
          original: u,
        };
      });
    return [{ id: INVALID_ID, value: t("noFocusArea"), original: null }, ...focusAreaOptions];
  }, [focusAreaList, t]);

  const renderItem = (option: ComboboxOption<FocusArea>): ReactElement => {
    const focusArea = option.original;

    const selected =
      (option.id === INVALID_ID && selectedFocusArea === null) || selectedFocusArea?.focusAreaId === option.id;
    return <FocusAreaSelectDropdownItem focusArea={focusArea} selected={selected} />;
  };

  const handleUserSelected = (selectedOption: ComboboxOption<FocusArea> | null) => {
    if (!selectedOption) {
      return;
    }

    if (selectedOption.id === INVALID_ID) {
      setSelectedFocusArea(null);
      if (onSelect) {
        onSelect(null);
      }
      return;
    }

    setSelectedFocusArea({ focusAreaId: selectedOption.id });
    if (onSelect) {
      onSelect(selectedOption);
    }
  };

  useEffect(() => {
    if (selected) {
      setSelectedFocusArea(selected);
    }
  }, [selected]);

  if (!comboboxOptions) {
    return <></>;
  }

  return (
    <Combobox<FocusArea>
      asChild={asChild}
      options={comboboxOptions}
      renderItem={renderItem}
      onSelect={handleUserSelected}
      side={side}
      isReadOnly={isReadOnly}
    >
      {children}
    </Combobox>
  );
};
